import React, { useState, useEffect, useRef } from "react";
import { Button, Badge, Text, SlideOver, Table } from "@teals/ui-web";
import type { Column } from "@teals/ui-web";
import type { DataSource, SchemaField } from "../../store/api/dataSourcesApi";
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
  BYO_PROVIDER_LABELS,
} from "./constants";
import {
  DetailSection,
  DetailSectionTitle,
  DetailField,
  DetailFieldLabel,
  DetailFieldValue,
  AssociationItem,
  AssociationName,
  Divider,
  UploadZone,
  UploadZoneIcon,
  UploadZoneText,
  UploadZoneHint,
  TabBar,
  Tab,
} from "./styles";

type TabKey = "overview" | "upload";

interface DataSourceDetailProps {
  ds: DataSource | null;
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabKey;
  deleteConfirm?: string | null;
  onDeleteConfirm?: (id: string | null) => void;
  onDelete?: (id: string) => void;
}

const schemaColumns: Column<SchemaField>[] = [
  { key: "name", header: "Field Name" },
  { key: "type", header: "Type" },
  {
    key: "searchable",
    header: "Searchable",
    align: "center",
    render: (val: boolean) => (
      <Badge variant={val ? "success" : "neutral"} size="sm">
        {val ? "Yes" : "No"}
      </Badge>
    ),
  },
  {
    key: "indexable",
    header: "Indexable",
    align: "center",
    render: (val: boolean) => (
      <Badge variant={val ? "success" : "neutral"} size="sm">
        {val ? "Yes" : "No"}
      </Badge>
    ),
  },
];

const DataSourceDetail: React.FC<DataSourceDetailProps> = ({
  ds,
  isOpen,
  onClose,
  initialTab = "overview",
  deleteConfirm,
  onDeleteConfirm,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setActiveTab(initialTab);
    setSelectedFile(null);
  }, [ds, initialTab]);

  const isByo = ds?.dsType === "byo";
  const providerLabel = ds?.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  const recordLabel =
    ds && ds.recordCount != null && ds.recordCount > 0
      ? ds.recordCount.toLocaleString()
      : "0";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleZoneClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={ds?.name ?? ""}
      width="lg"
      position="right"
    >
      {ds && (
        <>
          <TabBar>
            <Tab
              $active={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </Tab>
            {!isByo && (
              <Tab
                $active={activeTab === "upload"}
                onClick={() => setActiveTab("upload")}
              >
                Upload Data
              </Tab>
            )}
          </TabBar>

          {activeTab === "overview" && (
            <>
              <DetailSection>
                <DetailSectionTitle>Data Source Info</DetailSectionTitle>
                <DetailField>
                  <DetailFieldLabel>Name</DetailFieldLabel>
                  <DetailFieldValue>{ds.name}</DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Type</DetailFieldLabel>
                  <DetailFieldValue>
                    <Badge
                      variant={DS_TYPE_BADGE_VARIANT[ds.dsType] as any}
                      size="sm"
                    >
                      {DS_TYPE_LABELS[ds.dsType]}
                    </Badge>
                    {isByo && providerLabel && (
                      <Badge
                        variant="neutral"
                        size="sm"
                        style={{ marginLeft: 6 }}
                      >
                        {providerLabel}
                      </Badge>
                    )}
                  </DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Data Source ID</DetailFieldLabel>
                  <DetailFieldValue style={{ fontFamily: "monospace" }}>
                    {ds.dataSourceId}
                  </DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Records</DetailFieldLabel>
                  <DetailFieldValue>{recordLabel}</DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Status</DetailFieldLabel>
                  <DetailFieldValue>
                    <Badge
                      variant={ds.status === "active" ? "success" : "warning"}
                      size="sm"
                    >
                      {ds.status}
                    </Badge>
                  </DetailFieldValue>
                </DetailField>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Schema</DetailSectionTitle>
                {ds.schemaDefinition?.fields?.length ? (
                  <Table
                    columns={schemaColumns}
                    data={ds.schemaDefinition.fields}
                    rowKey="name"
                  />
                ) : (
                  <Text
                    variant="body1"
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.8125rem",
                    }}
                  >
                    No schema fields defined
                  </Text>
                )}
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Agent Associations</DetailSectionTitle>
                {ds.agentIds.length > 0 ? (
                  ds.agentIds.map((agentId) => (
                    <AssociationItem key={agentId}>
                      <AssociationName>{agentId}</AssociationName>
                      <Button variant="ghost" size="sm">
                        Remove
                      </Button>
                    </AssociationItem>
                  ))
                ) : (
                  <Text
                    variant="body1"
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.8125rem",
                    }}
                  >
                    No agents associated
                  </Text>
                )}
                <Button variant="secondary" size="sm" style={{ marginTop: 8 }}>
                  + Add Agent
                </Button>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Organisation</DetailSectionTitle>
                <AssociationItem>
                  <AssociationName>{ds.orgName}</AssociationName>
                  <Badge variant="success" size="sm">
                    Active
                  </Badge>
                </AssociationItem>
              </DetailSection>

              <Divider />

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {deleteConfirm === ds.dataSourceId ? (
                  <>
                    <Text
                      variant="body1"
                      style={{
                        color: "var(--danger, #dc2626)",
                        marginRight: 12,
                        alignSelf: "center",
                      }}
                    >
                      Are you sure?
                    </Text>
                    <Button
                      variant="ghost"
                      onClick={() => onDeleteConfirm?.(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => onDelete?.(ds.dataSourceId)}
                      style={{
                        background: "var(--danger, #dc2626)",
                        marginLeft: 8,
                      }}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => onDeleteConfirm?.(ds.dataSourceId)}
                    style={{ color: "var(--danger, #dc2626)" }}
                  >
                    Delete Data Source
                  </Button>
                )}
              </div>
            </>
          )}

          {activeTab === "upload" && !isByo && (
            <>
              <Text
                variant="body1"
                style={{ marginBottom: 16, color: "var(--text-secondary)" }}
              >
                Manually upload records to <strong>{ds.name}</strong>. For
                production ingestion, use the Direct API with your API Key
                Profile credentials.
              </Text>

              <UploadZone onClick={handleZoneClick}>
                <UploadZoneIcon>{"\uD83D\uDCC2"}</UploadZoneIcon>
                <UploadZoneText>
                  Drop JSON or CSV files here, or click to browse
                </UploadZoneText>
                <UploadZoneHint>Max 10 MB per file</UploadZoneHint>
              </UploadZone>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {selectedFile && (
                <Text
                  variant="body1"
                  style={{
                    marginTop: 12,
                    fontSize: "0.875rem",
                    color: "var(--text-primary)",
                  }}
                >
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)}{" "}
                  KB) — ready to upload
                </Text>
              )}
            </>
          )}
        </>
      )}
    </SlideOver>
  );
};

export default DataSourceDetail;
